import React, { useState, useEffect } from "react";
import { Modal, Tab, Tabs } from "react-bootstrap";
import { useEmailSequence, EmailMessage, SequenceType } from "../../../../hooks/useEmailSequence";

interface Props {
  show: boolean;
  handleClose: () => void;
  projectId: string;
  projectTitle: string;
}

const SEQUENCE_TABS: { key: SequenceType; label: string; icon: string; description: string }[] = [
  {
    key: "onboarding",
    label: "Onboarding",
    icon: "bi-envelope-check",
    description: "Welcome new users and guide them to their first value moment",
  },
  {
    key: "nurture",
    label: "Nurture",
    icon: "bi-graph-up-arrow",
    description: "Educate subscribers and build trust over time",
  },
  {
    key: "reengagement",
    label: "Re-engagement",
    icon: "bi-arrow-repeat",
    description: "Win back users who have gone quiet",
  },
];

const formatEmailAsMarkdown = (email: EmailMessage, index: number): string => {
  const lines = [
    `## Email ${index + 1}${email.dayOffset > 0 ? ` (Day ${email.dayOffset})` : " (Immediate)"}`,
    ``,
    `**Subject:** ${email.subject}`,
    `**Preview:** ${email.previewText}`,
    ``,
    email.body,
    ``,
    `**CTA:** ${email.ctaText}${email.ctaUrl ? ` → ${email.ctaUrl}` : ""}`,
  ];
  return lines.join("\n");
};

const downloadMarkdown = (content: string, filename: string) => {
  const blob = new Blob([content], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const EmailCard: React.FC<{ email: EmailMessage; index: number }> = ({ email, index }) => {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const text = formatEmailAsMarkdown(email, index);
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        marginBottom: "12px",
        overflow: "hidden",
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      }}
    >
      {/* Card header */}
      <div
        style={{
          padding: "14px 18px",
          display: "flex",
          alignItems: "flex-start",
          gap: "12px",
          cursor: "pointer",
          background: expanded ? "#F8FAFC" : "#FFFFFF",
          transition: "background 150ms ease-out",
        }}
        onClick={() => setExpanded(!expanded)}
      >
        {/* Day badge */}
        <div
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "8px",
            background: "#DBEAFE",
            color: "#3B82F6",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            fontSize: "11px",
            fontWeight: 700,
          }}
        >
          {email.dayOffset === 0 ? "D0" : `D${email.dayOffset}`}
        </div>

        {/* Subject + preview */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: "14px", fontWeight: 600, color: "#0F172A", marginBottom: "2px" }}>
            {email.subject}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "#94A3B8",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {email.previewText}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
          <button
            className="btn btn-sm"
            style={{
              padding: "4px 10px",
              fontSize: "12px",
              fontWeight: 600,
              color: "#3B82F6",
              border: "1px solid #DBEAFE",
              borderRadius: "6px",
              background: "transparent",
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleCopy();
            }}
          >
            <i className={`bi ${copied ? "bi-check2" : "bi-clipboard"} me-1`} />
            {copied ? "Copied" : "Copy"}
          </button>
          <button
            style={{
              background: "none",
              border: "none",
              padding: "4px 6px",
              cursor: "pointer",
              color: "#94A3B8",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
          >
            <i className={`bi ${expanded ? "bi-chevron-up" : "bi-chevron-down"}`} style={{ fontSize: "14px" }} />
          </button>
        </div>
      </div>

      {/* Body (collapsible) */}
      {expanded && (
        <div style={{ padding: "0 18px 18px 18px", borderTop: "1px solid #E2E8F0" }}>
          <div
            style={{
              paddingTop: "14px",
              fontSize: "13px",
              color: "#475569",
              lineHeight: 1.65,
              whiteSpace: "pre-wrap",
            }}
          >
            {email.body}
          </div>
          <div
            style={{
              marginTop: "14px",
              padding: "10px 14px",
              background: "#F1F5F9",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <i className="bi bi-cursor-fill" style={{ color: "#3B82F6", fontSize: "13px" }} />
            <span style={{ fontSize: "13px", fontWeight: 600, color: "#0F172A" }}>
              {email.ctaText}
            </span>
            {email.ctaUrl && (
              <span style={{ fontSize: "12px", color: "#94A3B8", marginLeft: "4px" }}>
                {email.ctaUrl}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const SequencePanel: React.FC<{
  emails: EmailMessage[];
  sequenceType: SequenceType;
  projectTitle: string;
}> = ({ emails, sequenceType, projectTitle }) => {
  const handleExport = () => {
    const labelMap: Record<SequenceType, string> = {
      onboarding: "Onboarding",
      nurture: "Nurture",
      reengagement: "Re-engagement",
    };
    const content =
      `# ${projectTitle} — ${labelMap[sequenceType]} Email Sequence\n\n` +
      emails.map((e, i) => formatEmailAsMarkdown(e, i)).join("\n\n---\n\n");
    downloadMarkdown(content, `${projectTitle.toLowerCase().replace(/\s+/g, "-")}-${sequenceType}-emails.md`);
  };

  const handleCopyAll = () => {
    const content = emails.map((e, i) => formatEmailAsMarkdown(e, i)).join("\n\n---\n\n");
    navigator.clipboard.writeText(content);
  };

  return (
    <div className="py-3">
      {/* Sequence summary */}
      <div
        style={{
          background: "#F8FAFC",
          border: "1px solid #E2E8F0",
          borderRadius: "10px",
          padding: "14px 18px",
          marginBottom: "20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div>
          <span style={{ fontSize: "13px", color: "#475569" }}>
            <i className="bi bi-envelope me-2 text-primary" />
            <strong>{emails.length} emails</strong> in this sequence
          </span>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            className="btn btn-sm"
            style={{
              padding: "6px 14px",
              fontSize: "13px",
              fontWeight: 600,
              color: "#475569",
              border: "1px solid #E2E8F0",
              borderRadius: "7px",
              background: "#FFFFFF",
            }}
            onClick={handleCopyAll}
          >
            <i className="bi bi-clipboard me-1" />
            Copy All
          </button>
          <button
            className="btn btn-sm"
            style={{
              padding: "6px 14px",
              fontSize: "13px",
              fontWeight: 600,
              color: "#3B82F6",
              border: "1px solid #DBEAFE",
              borderRadius: "7px",
              background: "#FFFFFF",
            }}
            onClick={handleExport}
          >
            <i className="bi bi-file-earmark-arrow-down me-1" />
            Export .md
          </button>
        </div>
      </div>

      {/* Email cards */}
      {emails.map((email, idx) => (
        <EmailCard key={email.id || idx} email={email} index={idx} />
      ))}
    </div>
  );
};

export const EmailSequenceModal: React.FC<Props> = ({
  show,
  handleClose,
  projectId,
  projectTitle,
}) => {
  const [activeTab, setActiveTab] = useState<string>("generate");

  const { isLoading, error, sequences, generateSequences, clearError, reset } = useEmailSequence();

  useEffect(() => {
    if (show) {
      reset();
      setActiveTab("generate");
    }
  }, [show, reset]);

  const handleGenerate = async () => {
    await generateSequences(projectId);
    setActiveTab("onboarding");
  };

  const handleExportAll = () => {
    if (!sequences) return;
    const sections = [
      `# ${projectTitle} — Email Sequences\n\n`,
      `## Onboarding Sequence\n\n` +
        sequences.onboarding.map((e, i) => formatEmailAsMarkdown(e, i)).join("\n\n---\n\n"),
      `\n\n## Nurture Sequence\n\n` +
        sequences.nurture.map((e, i) => formatEmailAsMarkdown(e, i)).join("\n\n---\n\n"),
      `\n\n## Re-engagement Sequence\n\n` +
        sequences.reengagement.map((e, i) => formatEmailAsMarkdown(e, i)).join("\n\n---\n\n"),
    ];
    downloadMarkdown(sections.join(""), `${projectTitle.toLowerCase().replace(/\s+/g, "-")}-all-emails.md`);
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered backdrop="static" scrollable>
      <Modal.Header closeButton style={{ border: "none", paddingBottom: 0 }}>
        <Modal.Title style={{ fontWeight: 700, color: "#0F172A", fontSize: "18px" }}>
          <i className="bi bi-envelope-at me-2 text-primary" />
          Email Sequence Generator
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ paddingTop: "12px" }}>
        {error && (
          <div className="alert alert-danger mb-4 d-flex align-items-center">
            <i className="bi bi-exclamation-triangle me-2" />
            <span className="flex-grow-1">{error}</span>
            <button type="button" className="btn-close" onClick={clearError} />
          </div>
        )}

        <Tabs
          activeKey={activeTab}
          onSelect={(k) => k && setActiveTab(k)}
          className="mb-4"
        >
          {/* Generate tab */}
          <Tab eventKey="generate" title="Generate">
            <div className="py-5 text-center">
              <div className="mb-5">
                <div
                  style={{
                    width: "96px",
                    height: "96px",
                    background: "#DBEAFE",
                    borderRadius: "9999px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "20px",
                  }}
                >
                  <i className="bi bi-envelope-at" style={{ fontSize: "40px", color: "#3B82F6" }} />
                </div>
                <h3 style={{ fontSize: "20px", fontWeight: 700, color: "#0F172A", marginBottom: "8px" }}>
                  AI Email Sequences
                </h3>
                <p style={{ fontSize: "14px", color: "#475569", maxWidth: "480px", margin: "0 auto" }}>
                  Generate three complete email sequences for{" "}
                  <strong>{projectTitle}</strong> — tailored to your validated
                  market, customer persona, and value proposition.
                </p>
              </div>

              <div
                style={{
                  background: "#DBEAFE",
                  borderRadius: "12px",
                  padding: "20px 24px",
                  maxWidth: "560px",
                  margin: "0 auto 32px",
                  textAlign: "left",
                }}
              >
                <h6 style={{ fontWeight: 700, color: "#1D4ED8", marginBottom: "12px" }}>
                  <i className="bi bi-magic me-2" />
                  What will be generated
                </h6>
                <div className="row">
                  {SEQUENCE_TABS.map((s) => (
                    <div key={s.key} className="col-md-4 mb-2">
                      <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                        <i className={`bi ${s.icon}`} style={{ color: "#3B82F6", marginTop: "2px" }} />
                        <div>
                          <div style={{ fontSize: "13px", fontWeight: 600, color: "#1E3A5F" }}>
                            {s.label}
                          </div>
                          <div style={{ fontSize: "11px", color: "#3B82F6" }}>{s.description}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button
                className="btn btn-primary btn-lg"
                style={{ padding: "12px 32px", fontWeight: 600, fontSize: "15px", borderRadius: "8px" }}
                onClick={handleGenerate}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Generating sequences…
                  </>
                ) : (
                  <>
                    <i className="bi bi-lightning me-2" />
                    Generate Email Sequences
                  </>
                )}
              </button>
            </div>
          </Tab>

          {/* Sequence tabs — only available after generation */}
          {SEQUENCE_TABS.map((seq) => (
            <Tab
              key={seq.key}
              eventKey={seq.key}
              title={
                <span>
                  <i className={`bi ${seq.icon} me-1`} />
                  {seq.label}
                  {sequences && (
                    <span
                      className="ms-2 badge"
                      style={{
                        background: "#DBEAFE",
                        color: "#3B82F6",
                        fontSize: "10px",
                        fontWeight: 700,
                        borderRadius: "9999px",
                        padding: "1px 6px",
                      }}
                    >
                      {sequences[seq.key].length}
                    </span>
                  )}
                </span>
              }
              disabled={!sequences}
            >
              {sequences && (
                <SequencePanel
                  emails={sequences[seq.key]}
                  sequenceType={seq.key}
                  projectTitle={projectTitle}
                />
              )}
            </Tab>
          ))}
        </Tabs>
      </Modal.Body>

      <Modal.Footer style={{ border: "none", paddingTop: 0 }}>
        <button
          className="btn"
          style={{
            fontWeight: 600,
            fontSize: "14px",
            color: "#475569",
            border: "1px solid #E2E8F0",
            borderRadius: "8px",
            padding: "8px 18px",
          }}
          onClick={handleClose}
        >
          Close
        </button>
        {sequences && (
          <>
            <button
              className="btn"
              style={{
                fontWeight: 600,
                fontSize: "14px",
                color: "#3B82F6",
                border: "1px solid #DBEAFE",
                borderRadius: "8px",
                padding: "8px 18px",
                background: "transparent",
              }}
              onClick={handleExportAll}
            >
              <i className="bi bi-file-earmark-arrow-down me-1" />
              Export All Sequences
            </button>
            <button
              className="btn btn-primary"
              style={{ fontWeight: 600, fontSize: "14px", padding: "8px 18px", borderRadius: "8px" }}
              onClick={handleGenerate}
              disabled={isLoading}
            >
              <i className="bi bi-arrow-clockwise me-1" />
              Regenerate
            </button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};
